import { CardFailedReason } from '../../shared/types';

/**
 * True ⇒ skip the event in feedback layers. Drops zero/negative damage
 * (shield absorbs route through `shieldBroken`; heals through `HealApplied`).
 * Misses (`missed: true`) carry `damage: 0` but still need a "MISS" marker,
 * so they pass through.
 */
export function shouldIgnoreDamageEvent(
  data: { damage?: unknown; missed?: unknown },
): boolean {
  if (data.missed === true) return false;
  const damage = typeof data.damage === 'number' ? data.damage : 0;
  return damage <= 0;
}

export interface LocalGateState {
  localSessionId: string;
  localDroneIds: ReadonlySet<string>;
}

export interface DamageEventData {
  targetDroneId?: unknown;
  attackerDroneId?: unknown;
  droneId?: unknown;
  killerId?: unknown;
  playerId?: unknown;
}

/** True when a HealApplied event is the Healer's own self-heal tick
 * (the `healer_aura` passive emits an event with healer === target).
 * Drives the gold/distinct self-heal aura in {@link EffectsManager.showSelfHealAura}.
 */
export function isSelfHeal(data: {
  healerDroneId?: unknown;
  targetDroneId?: unknown;
}): boolean {
  const h = data.healerDroneId;
  const t = data.targetDroneId;
  return typeof h === 'string' && typeof t === 'string' && h === t;
}

export function isLocalPlayerInvolved(
  data: DamageEventData,
  state: LocalGateState,
): boolean {
  if (data.playerId === state.localSessionId) return true;
  if (data.killerId === state.localSessionId) return true;
  for (const k of ['targetDroneId', 'attackerDroneId', 'droneId'] as const) {
    const id = data[k];
    if (typeof id === 'string' && state.localDroneIds.has(id)) return true;
  }
  return false;
}

/**
 * Map a `CardEffectFailed` reason code to a human-readable HUD toast string.
 * Unknown reasons fall back to a generic message so the player still gets
 * feedback that their card didn't run.
 */
export function cardFailedToastMessage(reason: string): string {
  switch (reason) {
    case CardFailedReason.Scattered:
      return 'Ultimate scattered — regroup!';
    case CardFailedReason.ConvertShotRequiresSniper:
      return 'ConvertShot needs a Sniper';
    default:
      return 'Card effect failed';
  }
}

/**
 * Route a `CardEffectFailed` server event to the local HUD. Skips events
 * for other players (the toast is a personal feedback channel). Returns
 * true when the toast was dispatched, false when the event was ignored.
 */
export function routeCardEffectFailed(
  data: { playerId?: unknown; reason?: unknown },
  localSessionId: string,
  showToast: (msg: string) => void,
): boolean {
  if (data.playerId !== localSessionId) return false;
  const reason = typeof data.reason === 'string' ? data.reason : '';
  showToast(cardFailedToastMessage(reason));
  return true;
}
